/**
 * Read-only decision support for one VulnerabilityEvent.
 *
 * The same blocks are shown in the expanded table row (where an auditor must be
 * able to read everything, audit trail included) and inside the Bewertung dialog
 * (where they are the basis for the verdict). Keeping them in one place is what
 * guarantees the dialog can never show *less* than the row it was opened from.
 *
 * Every timestamp here is a raw `CmdbDateTime` — rendered through `fmtDate` /
 * `fmtDay`, never through `new Date(...)`.
 */

import type { ReactNode } from 'react';

import CveLink from '../../components/CveLink';
import Chip, { ChipList } from '../../components/Chip';
import DataTable, { type DataTableColumn } from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import Icon from '../../components/Icon';
import { DASH, fmtDate, fmtDay, fmtScore, fmtValue } from '../../lib/format';
import type {
  BenachrichtigungStatus,
  BetroffeneInstanz,
  VulnerabilityEventRow,
} from '../../lib/types';

import {
  BEWERTUNG_LABELS,
  BEWERTUNG_TONE,
  kundenOf,
  parseScore,
  produktVersionLabel,
  scoreTone,
} from './eventHelpers';

const BENACHRICHTIGUNG_STATUS_LABELS: Readonly<Record<BenachrichtigungStatus, string>> = {
  OFFEN: 'Offen',
  VERSENDET: 'Versendet',
  GEMELDET: 'Gemeldet',
  ABGESCHLOSSEN: 'Abgeschlossen',
};

interface FactProps {
  label: string;
  children: ReactNode;
}

/** One `<dt>/<dd>` pair; renders an em dash instead of collapsing empty rows. */
function Fact({ label, children }: FactProps): ReactNode {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

export interface EventFactsProps {
  event: VulnerabilityEventRow;
}

/** CVE master data + Dependency-Track's native KEV assertion facts. */
export function EventCveFacts({ event }: EventFactsProps): ReactNode {
  const score = parseScore(event.cve.base_score);
  const kev = event.kev;
  const kevKnown =
    kev.name !== null ||
    kev.beschreibung !== null ||
    kev.aufgenommen_am !== null ||
    kev.empfohlene_massnahme !== null ||
    kev.sources.length > 0;

  return (
    <div className="triage-section">
      <h3>Schwachstelle</h3>
      <dl className="detail-list">
        <Fact label="CVE">
          <ChipList>
            <CveLink cve={event.cve.id} />
            {score === null ? null : (
              <Chip tone={scoreTone(score)}>
                CVSS {fmtScore(score)}
              </Chip>
            )}
            {event.cve.aktiv_ausgenutzt ? (
              <Chip tone="alert">
                aktiv ausgenutzt
              </Chip>
            ) : null}
          </ChipList>
        </Fact>
        <Fact label="Beschreibung">
          <span className={event.cve.beschreibung ? undefined : 'muted'}>
            {fmtValue(event.cve.beschreibung)}
          </span>
        </Fact>
        {kevKnown ? (
          <>
            <Fact label="KEV-Name">{fmtValue(kev.name)}</Fact>
            <Fact label="KEV-Beschreibung">{fmtValue(kev.beschreibung)}</Fact>
            <Fact label="Im KEV seit">{fmtDay(kev.aufgenommen_am)}</Fact>
            <Fact label="KEV-Quellen">
              {kev.sources.filter(Boolean).join(', ') || DASH}
            </Fact>
            <Fact label="Ransomware-Einsatz">
              {kev.ransomware === 'Known' ? (
                <Chip tone="alert">bekannt</Chip>
              ) : kev.ransomware === 'Not known' ? (
                <span className="muted">nicht bekannt</span>
              ) : (
                DASH
              )}
            </Fact>
            <Fact label="Empfohlene Maßnahme">{fmtValue(kev.empfohlene_massnahme)}</Fact>
          </>
        ) : (
          <Fact label="KEV">
            <span className="muted">
              Keine KEV-Details vorhanden: keine KEV-Assertion oder Abruf aus Dependency-Track
              fehlgeschlagen.
            </span>
          </Fact>
        )}
      </dl>
    </div>
  );
}

/** The affected KomponenteVersion and the ProduktVersionen that contain it. */
export function EventScopeFacts({ event }: EventFactsProps): ReactNode {
  const kunden = kundenOf(event);
  return (
    <div className="triage-section">
      <h3>Betroffener Umfang</h3>
      <dl className="detail-list">
        <Fact label="Komponentenversion">
          {fmtValue(event.komponente.name)}
          {event.komponente.version ? (
            <span className="muted"> {event.komponente.version}</span>
          ) : null}
          {event.komponente.hersteller ? (
            <div className="prod">Hersteller: {event.komponente.hersteller}</div>
          ) : null}
        </Fact>
        <Fact label="Enthalten in Produktversionen">
          {event.produktversionen.length === 0 ? (
            <span className="muted">In keiner Produktversion, nur im Katalog.</span>
          ) : (
            <ChipList>
              {event.produktversionen.map((version) => (
                <Chip key={version.id ?? produktVersionLabel(version)}>
                  {produktVersionLabel(version)}
                </Chip>
              ))}
            </ChipList>
          )}
        </Fact>
        <Fact label="Betroffene Kunden">
          {kunden.length === 0 ? (
            <span className="muted">Keine, es ist keine Produktinstanz ausgeliefert.</span>
          ) : (
            <ChipList>
              {kunden.map((kunde) => (
                <Chip key={kunde.id ?? kunde.name ?? '?'} title={kunde.email ?? 'Keine E-Mail hinterlegt'}>
                  {fmtValue(kunde.name)}
                </Chip>
              ))}
            </ChipList>
          )}
        </Fact>
        <Fact label="Betroffene Produktinstanzen">
          {event.betroffene.length === 0 ? (
            <span className="muted">Keine</span>
          ) : (
            `${event.betroffene.length}`
          )}
        </Fact>
      </dl>
    </div>
  );
}

/** Verdict, who recorded it, when, why — plus the notification counters. */
export function EventAuditTrail({ event }: EventFactsProps): ReactNode {
  return (
    <div className="triage-section">
      <h3>Audit-Trail</h3>
      <dl className="detail-list">
        <Fact label="Bewertung">
          <Chip tone={BEWERTUNG_TONE[event.bewertung]}>{BEWERTUNG_LABELS[event.bewertung]}</Chip>
        </Fact>
        <Fact label="Bewertet von">{fmtValue(event.bewertet_von)}</Fact>
        <Fact label="Bewertet am">{fmtDate(event.bewertet_am)}</Fact>
        <Fact label="Begründung">
          {event.begruendung ? (
            <blockquote className="triage-quote">{event.begruendung}</blockquote>
          ) : (
            <span className="muted">Keine Begründung erfasst.</span>
          )}
        </Fact>
        <Fact label="Event erstellt am">{fmtDate(event.erstellt_am)}</Fact>
        <Fact label="Benachrichtigungen">
          {event.benachrichtigungen_versendet === 0 && event.benachrichtigungen_offen === 0 ? (
            <span className="muted">Keine Benachrichtigung angelegt.</span>
          ) : (
            <>
              <Chip tone="good">{event.benachrichtigungen_versendet} versendet</Chip>
              {event.benachrichtigungen_offen > 0 ? (
                <span className="prod">{event.benachrichtigungen_offen} ausstehend</span>
              ) : null}
            </>
          )}
        </Fact>
      </dl>
    </div>
  );
}

const INSTANCE_COLUMNS: readonly DataTableColumn<BetroffeneInstanz>[] = [
  {
    key: 'serial',
    header: 'Seriennummer',
    render: (instance) => <span className="mono">{fmtValue(instance.seriennummer)}</span>,
  },
  {
    key: 'product',
    header: 'Produkt',
    render: (instance) => fmtValue(instance.produkttyp),
  },
  {
    key: 'version',
    header: 'Produktversion',
    render: (instance) => instance.produktversion ? `V${instance.produktversion}` : DASH,
  },
  {
    key: 'customer',
    header: 'Kunde',
    render: (instance) => fmtValue(instance.kunde.name),
  },
  {
    key: 'email',
    header: 'Kontakt-E-Mail',
    render: (instance) => instance.kunde.email || <span className="muted"><Icon name="warning" size={14} /> keine Kontakt-E-Mail</span>,
  },
  {
    key: 'notification',
    header: 'Benachrichtigung',
    render: (instance) => {
      const notification = instance.benachrichtigung;
      return notification === null ? <span className="muted">nicht angelegt</span> : (
        <>
          <Chip tone={notification.status === 'OFFEN' ? 'warn' : 'good'}>
            {BENACHRICHTIGUNG_STATUS_LABELS[notification.status] ?? notification.status}
          </Chip>
          <div className="prod">
            {notification.id === null ? 'ohne ID' : `#${notification.id}`}
            {notification.status === 'OFFEN' ? '' : ` · ${fmtDate(notification.versendet_am)}`}
          </div>
        </>
      );
    },
  },
];

/** Per-instance table: who has it, on which version, and its notification state. */
export function EventInstanzen({ event }: EventFactsProps): ReactNode {
  return (
    <div className="triage-section">
      <h3>Ausgelieferte Produktinstanzen ({event.betroffene.length})</h3>
      {event.betroffene.length === 0 ? (
        <EmptyState
          compact
          title="Keine Produktinstanz läuft nach bekannter Konfiguration mit dieser Komponentenversion"
        />
      ) : (
        <div className="triage-instance-table">
          <DataTable
            rows={event.betroffene}
            columns={INSTANCE_COLUMNS}
            rowKey={(instance) => instance.instanz_id
              ?? `${instance.seriennummer ?? 'sn'}-${event.betroffene.indexOf(instance)}`}
            mobileBreakpoint={760}
            pageSize={10}
            paginationResetKey={String(event.id)}
            resetPageOnRowsChange={false}
            caption="Betroffene Produktinstanzen und Benachrichtigungen"
          />
        </div>
      )}
    </div>
  );
}
